import React from "react";
import { itemWeight, scaleLabels } from "./templateUtils";

const round2 = (n) => Math.round(n * 100) / 100;
const range = (min, max) => {
  const out = [];
  for (let v = min; v <= max; v++) out.push(v);
  return out;
};

/**
 * Responses as the API wants them: empty list entries and empty table rows
 * are dropped (they'd count as "too few" anyway), everything else is kept.
 */
export const cleanResponses = (responses) =>
  Object.fromEntries(
    Object.entries(responses || {}).map(([key, r]) => {
      const next = { ...r };
      if (Array.isArray(r.entries))
        next.entries = r.entries.map((e) => e.trim()).filter(Boolean);
      if (Array.isArray(r.rows))
        next.rows = r.rows.filter((row) =>
          Object.values(row).some((v) => v && String(v).trim()),
        );
      return [key, next];
    }),
  );

/* ---------- Response updaters (immutable) ---------- */
const withSection = (responses, key, changes) => ({
  ...responses,
  [key]: { ...responses[key], ...changes },
});
const withItem = (responses, sectionKey, itemKey, changes) => {
  const section = responses[sectionKey] || {};
  return withSection(responses, sectionKey, {
    items: {
      ...section.items,
      [itemKey]: { ...section.items?.[itemKey], ...changes },
    },
  });
};

/* ---------- RATING ---------- */
const RatingSection = ({
  section,
  response,
  selfResponse,
  sectionScore,
  editable,
  onChange,
  responses,
}) => {
  const min = Number(section.scale?.min ?? 1);
  const max = Number(section.scale?.max ?? 5);
  const labelOf = new Map(
    scaleLabels(section.scale || { min, max, labels: [] }).map((l) => [
      l.value,
      l.label,
    ]),
  );

  return (
    <>
      <div className="table-responsive">
        <table className="table eval-rating-table mb-0">
          <thead>
            <tr>
              <th>Item</th>
              <th className="text-end">Weight</th>
              <th>Rating</th>
              <th className="text-end">Points</th>
              {section.allowItemComments && (
                <th className="eval-col-comment">Critical incidents</th>
              )}
            </tr>
          </thead>
          <tbody>
            {(section.items || []).map((item) => {
              const answer = response?.items?.[item.key] || {};
              const rating = answer.rating ?? null;
              const self = selfResponse?.items?.[item.key]?.rating;
              const weight = itemWeight(section, item);
              const points =
                rating !== null && max ? round2((rating / max) * weight) : null;
              return (
                <tr key={item.key}>
                  <td>
                    <div className="fw-semibold">{item.title}</div>
                    {item.description && (
                      <div className="text-slate-500 fs-075">
                        {item.description}
                      </div>
                    )}
                    {item.kpis?.length > 0 && (
                      <div className="d-flex flex-wrap gap-1 mt-1">
                        {item.kpis.map((k) => (
                          <span key={k} className="chip chip--sm">
                            {k}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="text-end tabular-nums">{round2(weight)}%</td>
                  <td className="eval-col-rating">
                    {editable ? (
                      <div
                        className="eval-scale"
                        role="radiogroup"
                        aria-label={`Rating for ${item.title}`}
                      >
                        {range(min, max).map((v) => (
                          <button
                            key={v}
                            type="button"
                            role="radio"
                            aria-checked={rating === v}
                            className={`eval-scale-btn ${rating === v ? "active" : ""}`}
                            title={labelOf.get(v) || String(v)}
                            onClick={() =>
                              onChange(
                                withItem(responses, section.key, item.key, {
                                  rating: rating === v ? null : v,
                                }),
                              )
                            }
                          >
                            {v}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <span
                        className={`eval-rating-value ${rating === null ? "is-empty" : ""}`}
                      >
                        {rating ?? "—"}
                      </span>
                    )}
                    {rating !== null && labelOf.get(rating) && (
                      <div className="text-slate-500 fs-070 mt-1">
                        {labelOf.get(rating)}
                      </div>
                    )}
                    {self !== undefined && self !== null && (
                      <span
                        className="eval-self-chip"
                        title="Employee's self-assessment"
                      >
                        Self: {self}
                      </span>
                    )}
                  </td>
                  <td className="text-end tabular-nums">
                    {points === null ? "—" : `${points} / ${round2(weight)}`}
                  </td>
                  {section.allowItemComments && (
                    <td>
                      {editable ? (
                        <textarea
                          className="form-control form-control-sm"
                          rows={2}
                          maxLength={2000}
                          value={answer.comment || ""}
                          aria-label={`Critical incidents for ${item.title}`}
                          onChange={(e) =>
                            onChange(
                              withItem(responses, section.key, item.key, {
                                comment: e.target.value,
                              }),
                            )
                          }
                        />
                      ) : (
                        <span className="fs-080 white-space-pre">
                          {answer.comment || "—"}
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {section.allowSectionComment && (
        <div className="mt-2">
          <div className="approval-meta-label">Critical incidents</div>
          {editable ? (
            <textarea
              className="form-control"
              rows={2}
              maxLength={2000}
              value={response?.comment || ""}
              aria-label={`${section.title} critical incidents`}
              onChange={(e) =>
                onChange(
                  withSection(responses, section.key, {
                    comment: e.target.value,
                  }),
                )
              }
            />
          ) : (
            <div className="approval-note">{response?.comment || "—"}</div>
          )}
        </div>
      )}
      {sectionScore && (
        <div className="eval-section-score">
          {sectionScore.rated}/{sectionScore.totalItems} rated · Section score{" "}
          <strong>{sectionScore.score}</strong> / {sectionScore.weight}
        </div>
      )}
    </>
  );
};

/* ---------- TEXT_LIST ---------- */
const TextListSection = ({
  section,
  response,
  editable,
  onChange,
  responses,
}) => {
  const entries = response?.entries || [];
  const max = Number(section.maxEntries) || 10;
  if (!editable) {
    return entries.length ? (
      <ol className="eval-list">
        {entries.map((e, i) => (
          <li key={i}>{e}</li>
        ))}
      </ol>
    ) : (
      <p className="text-slate-400 fs-085 mb-0">—</p>
    );
  }
  const shown = Math.min(
    Math.max(entries.length, Number(section.minEntries) || 1, 1),
    max,
  );
  const values = Array.from({ length: shown }, (_, i) => entries[i] || "");
  const set = (next) =>
    onChange(withSection(responses, section.key, { entries: next }));
  return (
    <div className="d-flex flex-column gap-2">
      {values.map((v, i) => (
        <div key={i} className="d-flex gap-2 align-items-center">
          <span className="eval-list-number">{i + 1}.</span>
          <input
            className="form-control form-control-sm"
            value={v}
            maxLength={1000}
            aria-label={`${section.title} ${i + 1}`}
            onChange={(e) =>
              set(values.map((x, j) => (j === i ? e.target.value : x)))
            }
          />
          {values.length > 1 && (
            <button
              type="button"
              className="btn btn-sm btn-light text-danger"
              onClick={() => set(values.filter((_, j) => j !== i))}
              aria-label={`Remove entry ${i + 1}`}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          )}
        </div>
      ))}
      {values.length < max && (
        <button
          type="button"
          className="btn btn-sm btn-link p-0 align-self-start"
          onClick={() => set([...values, ""])}
        >
          <i className="bi bi-plus-lg"></i> Add entry
        </button>
      )}
    </div>
  );
};

/* ---------- TABLE ---------- */
const TableSection = ({ section, response, editable, onChange, responses }) => {
  const columns = section.columns || [];
  const rows = response?.rows || [];
  const max = Number(section.maxRows) || 10;
  const shown = editable ? (rows.length ? rows : [{}]) : rows;
  const set = (next) =>
    onChange(withSection(responses, section.key, { rows: next }));

  if (!editable && rows.length === 0)
    return <p className="text-slate-400 fs-085 mb-0">—</p>;
  return (
    <div className="table-responsive">
      <table className="table table-bordered table-sm eval-grid mb-0">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key}>{c.label}</th>
            ))}
            {editable && <th aria-label="Remove row"></th>}
          </tr>
        </thead>
        <tbody>
          {shown.map((row, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c.key}>
                  {editable ? (
                    <input
                      type={c.type === "date" ? "date" : "text"}
                      className="form-control form-control-sm"
                      value={row[c.key] || ""}
                      maxLength={1000}
                      aria-label={`${c.label}, row ${i + 1}`}
                      onChange={(e) =>
                        set(
                          shown.map((r, j) =>
                            j === i ? { ...r, [c.key]: e.target.value } : r,
                          ),
                        )
                      }
                    />
                  ) : (
                    row[c.key] || "—"
                  )}
                </td>
              ))}
              {editable && (
                <td className="text-center">
                  <button
                    type="button"
                    className="btn btn-sm btn-light text-danger"
                    onClick={() => set(shown.filter((_, j) => j !== i))}
                    aria-label={`Remove row ${i + 1}`}
                    disabled={
                      shown.length === 1 && !Object.values(row).some(Boolean)
                    }
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {editable && shown.length < max && (
        <button
          type="button"
          className="btn btn-sm btn-link p-0 mt-1"
          onClick={() => set([...shown, {}])}
        >
          <i className="bi bi-plus-lg"></i> Add row
        </button>
      )}
    </div>
  );
};

/* ---------- TEXT ---------- */
const TextSection = ({ section, response, editable, onChange, responses }) =>
  editable ? (
    <textarea
      className="form-control"
      rows={4}
      maxLength={10000}
      value={response?.text || ""}
      aria-label={section.title}
      onChange={(e) =>
        onChange(withSection(responses, section.key, { text: e.target.value }))
      }
    />
  ) : (
    <div className="approval-note">{response?.text || "—"}</div>
  );

const BODIES = {
  RATING: RatingSection,
  TEXT_LIST: TextListSection,
  TABLE: TableSection,
  TEXT: TextSection,
};

/**
 * The evaluation form (the template copy stored on the evaluation).
 * Read-only by default; `editable` + `onChange(nextResponses)` for HR encoding.
 */
const EvaluationForm = ({
  sections,
  responses,
  selfResponses,
  scores,
  editable,
  onChange,
}) => {
  const scoreByKey = new Map((scores?.sections || []).map((s) => [s.key, s]));
  const safe = responses || {};
  return (
    <div className="d-flex flex-column gap-3">
      {(sections || []).map((section) => {
        const Body = BODIES[section.type];
        return (
          <section
            key={section.key}
            className="eval-card"
            aria-labelledby={`sec-${section.key}`}
          >
            <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
              <div>
                <h3 id={`sec-${section.key}`} className="eval-section-title">
                  {section.title}
                  {section.required && section.type !== "RATING" && (
                    <span className="text-danger ms-1" title="Required">
                      *
                    </span>
                  )}
                </h3>
                {section.description && (
                  <div className="text-slate-500 fs-080">
                    {section.description}
                  </div>
                )}
              </div>
              {section.type === "RATING" && (
                <span className="section-weight-chip">{section.weight}%</span>
              )}
            </div>
            {Body && (
              <Body
                section={section}
                response={safe[section.key]}
                selfResponse={selfResponses?.[section.key]}
                sectionScore={scoreByKey.get(section.key)}
                editable={editable}
                onChange={onChange}
                responses={safe}
              />
            )}
          </section>
        );
      })}
    </div>
  );
};

export default EvaluationForm;
